import * as React from 'react'
import {AppBar, AppBarProps, useMediaQuery} from '@mui/material'
import {useTheme} from '@mui/material/styles'
import {observer} from 'mobx-react-lite'
import {toLocaleDateString} from '@oc/utils'
import {EcosystemLinks} from './EcosystemLinks'
import {OcAppBarMenu} from './OcAppBarMenu'
import {useOcAppBarState} from '../../Contexts/OcAppBarStateContext'
import {OcAppBarTimezoneMenuWithTime} from './OcAppBarTime'
import {useAuthInfo, useCustomerRouteContext, useDateTimezoneState} from '@oc/shell-core'
import {makeOcStyles} from '@oc/styles'
import {TimeZoneNotSet} from '../../Model/OcAppBarState'
import {ProfilePictureUploadDialog} from './ProfilePictureUploadDialog'
import {EditProfileDialog} from './EditProfileDialog/EditProfileDialog'

export const ocAppBarHeight = 56

const useAppBarStyles = makeOcStyles(theme => ({
    appBar: {
        height: ocAppBarHeight,
        maxHeight: ocAppBarHeight,
        flexDirection: 'row',
        paddingRight: theme.spacing(3),
        paddingLeft: theme.spacing(1.5), // combines with padding around timezone button
        [theme.breakpoints.down(700)]: {
            justifyContent: 'end'
        },
        [theme.breakpoints.down(1000)]: {
            background: `linear-gradient(90deg, ${theme.syskronPalette.colors.P900}, ${theme.syskronPalette.colors.P700}, ${theme.syskronPalette.colors.W50} 75%, ${theme.syskronPalette.colors.P50})`
        },
        [theme.breakpoints.between(1000, 1500)]: {
            background: `linear-gradient(90deg, ${theme.syskronPalette.colors.P900}, ${theme.syskronPalette.colors.P700}, ${theme.syskronPalette.colors.W50} 50%, ${theme.syskronPalette.colors.P100} 70%, ${theme.syskronPalette.colors.P50})`
        },
        [theme.breakpoints.up(1500)]: {
            background: `linear-gradient(90deg, ${theme.syskronPalette.colors.P900}, ${theme.syskronPalette.colors.P700}, ${theme.syskronPalette.colors.W50} 30%, ${theme.syskronPalette.colors.P100} 70%, ${theme.syskronPalette.colors.P50})`
        },
        justifyContent: 'space-between',
        borderBottom: `1px solid ${theme.syskronPalette.contrastColors.W50}`,
        minWidth: 360
    },
    flexDiv: {
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        padding: theme.spacing(1)
    },
    clockDiv: {
        height: '100%',
        width: 150,
        display: 'flex',
        flexDirection: 'column'
    },
    bottomTimeDetail: {
        paddingTop: 2
    },
    upperTimeDetail: {
        paddingTop: 3
    },
    flex: {
        display: 'flex'
    }
}))

function _OcAppBar(props: AppBarProps) {
    const {classes} = useAppBarStyles()
    const theme = useTheme()
    const state = useOcAppBarState()
    const dateTimezoneState = useDateTimezoneState()
    const {username} = useAuthInfo()

    const showTimeSettings = useMediaQuery(theme.breakpoints.up(700))
    const showEcosystemLinks = useMediaQuery(theme.breakpoints.up(1200))

    const {accountId, s2aAccount, s2aProxy, loading, customer} = useCustomerRouteContext()

    const timezone = customer?.customerTimezone ? (customer?.customerTimezone as string) : undefined

    React.useEffect(() => {
        if (!loading) {
            state.load(s2aProxy, s2aAccount ?? undefined, username, accountId || undefined)
        }
    }, [loading, state, s2aAccount, accountId, s2aProxy, username])

    React.useEffect(() => {
        const interval = setInterval(() => {
            state.localTimezoneTime = toLocaleDateString(new Date())
            state.customerTimezoneTime = toLocaleDateString(new Date(), timezone)
        }, 1000)
        return () => {
            clearInterval(interval)
        }
    }, [timezone, dateTimezoneState, state])

    React.useEffect(() => {
        state.customerTimezone = customer ? customer.customerTimezone || TimeZoneNotSet : undefined
    }, [timezone, state, customer?.customerTimezone, customer])

    return (
        <>
            <AppBar
                id="oc-app-bar"
                data-cy="oc-app-bar"
                position="static"
                className={classes.appBar}
                elevation={0}
                {...props}
            >
                {showTimeSettings ? (
                    <div className={classes.flex} key={'oc-app-bar-menu-change-timezone'}>
                        <OcAppBarTimezoneMenuWithTime />
                    </div>
                ) : null}
                {showEcosystemLinks ? <EcosystemLinks /> : null}
                <OcAppBarMenu includeEcosystemLinks={!showEcosystemLinks} includeTimeZoneSettings={!showTimeSettings} />
            </AppBar>
            <ProfilePictureUploadDialog />
            <EditProfileDialog />
        </>
    )
}

export const OcAppBar = observer(_OcAppBar)
